/**
 * gstValidation.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Standard GST validation utilities for Indian accounting compliance.
 * - GSTIN format + checksum validation
 * - HSN / SAC code format validation
 * - UQC (Unit Quantity Code) lookup for GSTR-1 HSN summary
 * - Place of Supply resolution
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── GSTIN Validation ─────────────────────────────────────────────────────────

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;

const GSTIN_CHECKSUM_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Validates GSTIN format and Luhn-like checksum.
 * Returns null if valid, or an error message string.
 */
export function validateGstin(gstin: string): string | null {
  if (!gstin) return null; // Empty is allowed (unregistered)
  const g = gstin.trim().toUpperCase();
  if (g.length !== 15) return `GSTIN must be exactly 15 characters (got ${g.length})`;
  if (!GSTIN_REGEX.test(g)) return 'Invalid GSTIN format. Expected: 2-digit state code + PAN + entity code + Z + checksum';

  // Checksum validation
  const computed = computeGstinChecksum(g.slice(0, 14));
  if (computed !== g[14]) return `Invalid GSTIN checksum. Expected last digit: ${computed}`;

  return null; // Valid
}

function computeGstinChecksum(gstin14: string): string {
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const c = GSTIN_CHECKSUM_CHARS.indexOf(gstin14[i]);
    if (c < 0) return '?';
    const product = c * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(product / 36) + (product % 36);
  }
  return GSTIN_CHECKSUM_CHARS[(36 - (sum % 36)) % 36];
}

/**
 * Decode GSTIN into human-readable parts.
 */
export function decodeGstin(gstin: string): { stateCode: string; stateName: string; pan: string; entityType: string; checksum: string } | null {
  if (!gstin || gstin.trim().length !== 15) return null;
  const g = gstin.trim().toUpperCase();
  const stateCode = g.slice(0, 2);
  const pan       = g.slice(2, 12);
  const entityCode = g[12];
  const checksum  = g[14];

  const entityMap: Record<string, string> = {
    '1': 'Individual/Proprietor', '2': 'Partnership',
    '3': 'HUF', '4': 'Company (Private)', '5': 'Public Company',
    '6': 'Government Entity', '7': 'LLP', '9': 'NRI',
    'A': 'AOP', 'B': 'BOI', 'C': 'Corporation',
    'F': 'Firm', 'G': 'Government', 'H': 'HUF',
    'L': 'Local Authority', 'J': 'Artificial Juridical Person',
    'T': 'Trust',
  };

  return {
    stateCode,
    stateName: STATE_CODE_TO_NAME[stateCode] || `State ${stateCode}`,
    pan,
    entityType: entityMap[entityCode] || `Entity ${entityCode}`,
    checksum,
  };
}

// ── HSN / SAC Code Validation ─────────────────────────────────────────────────

/**
 * Validates HSN (for goods) or SAC (for services) code format.
 * HSN: 4, 6, or 8 digits. SAC: 6 digits starting with 99.
 */
export function validateHsnSac(code: string): string | null {
  if (!code) return null;
  const c = code.trim();
  if (!/^\d+$/.test(c)) return 'HSN/SAC must contain only digits';
  if (![4, 6, 8].includes(c.length)) return 'HSN code must be 4, 6, or 8 digits; SAC code must be 6 digits';
  return null;
}

export function isServiceCode(code: string): boolean {
  return code.startsWith('99') && code.length === 6;
}

// ── UQC (Unit Quantity Code) ─────────────────────────────────────────────────
// Required for GSTR-1 HSN Summary Table

export const UQC_CODES: Record<string, string> = {
  'BAG':  'BAG-Bags',       'BAL':  'BAL-Bale',        'BDL':  'BDL-Bundles',
  'BKL':  'BKL-Buckles',    'BOU':  'BOU-Box',          'BOX':  'BOX-Box',
  'BTL':  'BTL-Bottles',    'BUN':  'BUN-Bunches',      'CAN':  'CAN-Cans',
  'CBM':  'CBM-Cubic Meter','CCM':  'CCM-Cubic Centimeter','CMS': 'CMS-Centimeters',
  'CTN':  'CTN-Cartons',    'DOZ':  'DOZ-Dozens',       'DRM':  'DRM-Drums',
  'GGK':  'GGK-Great Gross','GMS':  'GMS-Grams',        'GRS':  'GRS-Gross',
  'GYD':  'GYD-Gross Yards','KGS':  'KGS-Kilograms',   'KLR':  'KLR-Kilolitres',
  'KME':  'KME-Kilometres', 'LTR':  'LTR-Litres',       'MLT':  'MLT-Millilitres',
  'MTR':  'MTR-Meters',     'MTS':  'MTS-Metric Tons',  'NOS':  'NOS-Numbers',
  'OTH':  'OTH-Others',     'PAC':  'PAC-Packs',        'PCS':  'PCS-Pieces',
  'PRS':  'PRS-Pairs',      'QTL':  'QTL-Quintals',     'ROL':  'ROL-Rolls',
  'SET':  'SET-Sets',       'SQF':  'SQF-Sq. Feet',     'SQM':  'SQM-Sq. Meters',
  'SQY':  'SQY-Sq. Yards',  'TBS':  'TBS-Tablets',      'TGM':  'TGM-Ten Gross',
  'THD':  'THD-Thousands',  'TON':  'TON-Tonnes',        'TUB':  'TUB-Tubes',
  'UGS':  'UGS-US Gallons', 'UNT':  'UNT-Units',        'YDS':  'YDS-Yards',
};

/**
 * Map common unit symbols to UQC codes for GSTR-1 HSN summary.
 */
export function unitSymbolToUqc(symbol: string): string {
  if (!symbol) return 'OTH';
  const s = symbol.trim().toUpperCase();
  const direct: Record<string, string> = {
    'KGS': 'KGS', 'KG': 'KGS', 'KILO': 'KGS',
    'LTR': 'LTR', 'L': 'LTR', 'LITRE': 'LTR', 'LITER': 'LTR',
    'MTR': 'MTR', 'M': 'MTR', 'METER': 'MTR', 'METRE': 'MTR',
    'NOS': 'NOS', 'NO': 'NOS', 'NUM': 'NOS', 'NUMS': 'NOS',
    'PCS': 'PCS', 'PC': 'PCS', 'PIECE': 'PCS', 'PIECES': 'PCS',
    'BOX': 'BOX', 'BX': 'BOX',
    'BTL': 'BTL', 'BOTTLE': 'BTL', 'BOTTLES': 'BTL',
    'PKT': 'PAC', 'PACKET': 'PAC', 'PAC': 'PAC', 'PACK': 'PAC',
    'BAG': 'BAG', 'BAGS': 'BAG',
    'DOZ': 'DOZ', 'DOZEN': 'DOZ', 'DZ': 'DOZ',
    'SET': 'SET', 'SETS': 'SET',
    'TON': 'TON', 'TONS': 'TON', 'TONNE': 'TON',
    'GMS': 'GMS', 'GM': 'GMS', 'GRAM': 'GMS', 'GRAMS': 'GMS',
    'MLT': 'MLT', 'ML': 'MLT', 'MILLILITRE': 'MLT',
    'CTN': 'CTN', 'CARTON': 'CTN', 'CARTONS': 'CTN',
    'ROL': 'ROL', 'ROLL': 'ROL', 'ROLLS': 'ROL',
    'SQM': 'SQM', 'SQF': 'SQF', 'SQY': 'SQY',
    'TBS': 'TBS', 'TAB': 'TBS', 'TABLET': 'TBS', 'TABLETS': 'TBS',
  };
  return direct[s] || 'OTH';
}

// ── State Code ↔ Name Maps ────────────────────────────────────────────────────

export const STATE_CODE_TO_NAME: Record<string, string> = {
  '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
  '04': 'Chandigarh',      '05': 'Uttarakhand',       '06': 'Haryana',
  '07': 'Delhi',           '08': 'Rajasthan',          '09': 'Uttar Pradesh',
  '10': 'Bihar',           '11': 'Sikkim',             '12': 'Arunachal Pradesh',
  '13': 'Nagaland',        '14': 'Manipur',            '15': 'Mizoram',
  '16': 'Tripura',         '17': 'Meghalaya',          '18': 'Assam',
  '19': 'West Bengal',     '20': 'Jharkhand',          '21': 'Odisha',
  '22': 'Chhattisgarh',    '23': 'Madhya Pradesh',     '24': 'Gujarat',
  '25': 'Daman & Diu',     '26': 'Dadra & Nagar Haveli and Daman & Diu',
  '27': 'Maharashtra',     '28': 'Andhra Pradesh (old)', '29': 'Karnataka',
  '30': 'Goa',             '31': 'Lakshadweep',        '32': 'Kerala',
  '33': 'Tamil Nadu',      '34': 'Puducherry',         '35': 'Andaman & Nicobar Islands',
  '36': 'Telangana',       '37': 'Andhra Pradesh',     '38': 'Ladakh',
  '97': 'Other Territory', '99': 'Centre Jurisdiction',
};

export const STATE_NAME_TO_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_CODE_TO_NAME).map(([code, name]) => [name, code])
);

/**
 * Extracts the 2-digit state code from a GSTIN, or looks up by state name.
 */
export function getStateCode(gstin?: string, stateName?: string): string {
  if (gstin && gstin.length >= 2) return gstin.slice(0, 2);
  if (stateName) return STATE_NAME_TO_CODE[stateName] || '';
  return '';
}

/**
 * Returns 'IGST' for inter-state, 'CGST_SGST' for intra-state, null if unknown.
 */
export function detectSupplyType(
  companyGstin?: string, companyState?: string,
  partyGstin?: string,   partyState?: string
): 'IGST' | 'CGST_SGST' | null {
  const compCode = getStateCode(companyGstin, companyState);
  const partyCode = getStateCode(partyGstin, partyState);
  if (!compCode || !partyCode) return null;
  return compCode === partyCode ? 'CGST_SGST' : 'IGST';
}
